/** Final-Synthesis Gate (Stage 8) — HF EU prompt schema. */
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Compass } from 'lucide-react';
import { asArrayG, asTextG, objG, VERDICT_CLS } from './gateConstants';
import { FindingRow } from './RichGateBits';
import { ConfidenceBadge } from './ConfidenceBadge';
import { PathCFailures } from './RichCompliancePathC';
import { LegalBasisPanel, type JurisdictionContext } from './LegalBasisPanel';
import { TypeMismatchCallout } from './TypeMismatchCallout';
import { ConsistencyMismatchPanel, type ConsistencyMismatch } from './ConsistencyMismatchPanel';

export function RichGateFinalBody({ output }: { output: Record<string, unknown> }) {
  const verdict = asTextG(output.pipeline_verdict) || 'REVIEW';
  const verdictCls = VERDICT_CLS[verdict] ?? '';
  const summary = asTextG(output.executive_summary);
  const risk = objG(output.overall_risk_assessment);
  const riskScore = risk.contract_risk_score;
  const riskCat = asTextG(risk.risk_category);
  const topRisks = asArrayG(risk.top_risk_factors);
  const consistency = objG(output.cross_agent_consistency);
  const inconsistencies = asArrayG(consistency.inconsistencies);
  const findings = asArrayG(output.key_findings);
  const recs = asArrayG(output.recommendations);
  const qualityScore = output.pipeline_quality_score;
  const reasons = asArrayG(output.human_review_reasons).filter(
    (x): x is string => typeof x === 'string',
  );
  const flags = objG(output.accumulated_flags);
  const allFlags = asArrayG(flags.all_flags).filter((f): f is string => typeof f === 'string');
  const critical = asArrayG(flags.critical_flags).filter((f): f is string => typeof f === 'string');
  // Recomputed by the final gate, so unlike Gate 1's copy this one carries the
  // legislation the compliance agent identified from the document.
  const jurisdiction = (output.jurisdiction_context ?? null) as JurisdictionContext | null;
  const mismatches = asArrayG(output.consistency_mismatches) as ConsistencyMismatch[];

  return (
    <div className="space-y-3">
      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Pipeline verdict</span>
            <Badge variant="outline" className={`text-xs ${verdictCls}`}>{verdict}</Badge>
            <span className="ml-auto">
              <ConfidenceBadge
                score={qualityScore}
                rationale={asTextG(output.gate_notes) || asTextG(output.pipeline_verdict_reason)}
                label="Quality"
              />
            </span>
          </div>
          {asTextG(output.pipeline_verdict_reason) && (
            <p className="text-xs text-muted-foreground">{asTextG(output.pipeline_verdict_reason)}</p>
          )}
          {output.human_review_required === true && (
            <div className="text-xs text-amber-800 dark:text-amber-300 mt-1">
              Human review required:
              {reasons.length > 0 && (
                <ul className="list-disc list-inside ml-2 mt-0.5">
                  {reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {summary && (
        <div className="rounded border bg-muted/30 p-3 text-sm">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Executive summary</p>
          <p className="whitespace-pre-wrap">{summary}</p>
        </div>
      )}

      <PathCFailures output={output} />

      <TypeMismatchCallout context={jurisdiction} />
      <ConsistencyMismatchPanel mismatches={mismatches} />
      <LegalBasisPanel context={jurisdiction} />

      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-1">
          <div className="flex items-center gap-2">
            <Compass className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium">Overall risk</span>
            {riskCat && (
              <Badge variant="outline" className={`text-[10px] ml-auto ${VERDICT_CLS[riskCat] ?? ''}`}>{riskCat}</Badge>
            )}
            {typeof riskScore === 'number' && (
              <span className="text-xs text-muted-foreground">{riskScore}/100</span>
            )}
          </div>
          {topRisks.length > 0 && (
            <ul className="space-y-0.5 text-xs">
              {topRisks.map((r, i) => {
                const o = objG(r);
                return (
                  <li key={i}>
                    <span className="text-muted-foreground">#{asTextG(o.rank) || (i + 1)} </span>
                    <span className="font-medium">{asTextG(o.description) || '—'}</span>
                    {asTextG(o.source_agent) && (
                      <span className="text-muted-foreground"> · {asTextG(o.source_agent)}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {inconsistencies.length > 0 && (
        <div className="rounded border border-orange-300 bg-orange-50 p-3 text-xs dark:border-orange-700 dark:bg-orange-950/30">
          <p className="font-medium text-orange-800 dark:text-orange-300 flex items-center gap-1 mb-1">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Cross-agent inconsistencies ({inconsistencies.length})
          </p>
          <ul className="space-y-0.5">
            {inconsistencies.map((it, i) => {
              const o = objG(it);
              return (
                <li key={i}>
                  <span className="font-medium">{asTextG(o.description) || '—'}</span>
                  {asTextG(o.recommended_resolution) && (
                    <span className="text-muted-foreground"> → {asTextG(o.recommended_resolution)}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {findings.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Key findings ({findings.length})</p>
          <ul className="space-y-1">{findings.map((f, i) => <FindingRow key={i} row={objG(f)} />)}</ul>
        </div>
      )}

      {recs.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Recommendations ({recs.length})</p>
          <ul className="space-y-1">{recs.map((r, i) => <FindingRow key={i} row={objG(r)} />)}</ul>
        </div>
      )}

      {(allFlags.length > 0 || critical.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {critical.map((f, i) => (
            <span key={`c${i}`} className="text-[10px] rounded bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 px-1.5 py-0.5">{f}</span>
          ))}
          {allFlags.filter((f) => !critical.includes(f)).map((f, i) => (
            <span key={`a${i}`} className="text-[10px] rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 px-1.5 py-0.5">{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}
